import { useState, useEffect, useCallback } from "react";
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import {
  type Address,
  encodeAbiParameters,
  formatUnits,
} from "viem";
import { Loader2, ExternalLink, Plus, ZoomIn } from "lucide-react";
import { ADDRESSES } from "../../config/contracts";
import {
  CL_POSITION_MANAGER_ABI,
  CL_POOL_MANAGER_ABI,
  CL_FEE_TIERS,
  CL_ACTIONS,
  sortCurrencies,
  clParameters,
  NO_HOOKS,
  priceToTick,
  tickToPrice,
  nearestUsableTick,
  getLiquidityForAmounts,
  type PoolKey,
} from "../../config/v4Contracts";
import { type TokenInfo, NATIVE_JASMY, DEFAULT_TOKEN_LIST } from "../../config/tokens";
import { TokenSelector } from "../swap/TokenSelector";
import { useTokenBalance } from "../../hooks/useTokenBalance";
import { useTokenAllowance } from "../../hooks/useTokenAllowance";
import {
  formatTokenAmount,
  getDeadline,
  parseTokenAmount,
  explorerUrl,
  cn,
} from "../../lib/utils";

// Compute keccak256 of PoolKey (simplified: deterministic concat for display only)
function buildPoolId(poolKey: PoolKey): `0x${string}` {
  // Use a simple deterministic string as display identifier
  return `0x${poolKey.currency0.slice(2)}${poolKey.currency1.slice(2)}${poolKey.fee.toString(16).padStart(6, "0")}` as `0x${string}`;
}

// Encode MINT_POSITION unlockData for CLPositionManager.modifyLiquidities
function encodeMintUnlockData(
  poolKey: PoolKey,
  tickLower: number,
  tickUpper: number,
  liquidity: bigint,
  amount0Max: bigint,
  amount1Max: bigint,
  recipient: Address
): `0x${string}` {
  // actions = bytes: [MINT_POSITION (0x00)]
  const actions = new Uint8Array([CL_ACTIONS.MINT_POSITION]);
  const actionsHex = `0x${Array.from(actions).map((b) => b.toString(16).padStart(2, "0")).join("")}` as `0x${string}`;

  // params[0] = abi.encode(poolKey struct, tickLower, tickUpper, liquidity, amount0Max, amount1Max, recipient, hookData)
  const mintParams = encodeAbiParameters(
    [
      {
        name: "poolKey",
        type: "tuple" as const,
        components: [
          { name: "currency0", type: "address" as const },
          { name: "currency1", type: "address" as const },
          { name: "hooks", type: "address" as const },
          { name: "poolManager", type: "address" as const },
          { name: "fee", type: "uint24" as const },
          { name: "parameters", type: "bytes32" as const },
        ] as const,
      },
      { name: "tickLower", type: "int24" as const },
      { name: "tickUpper", type: "int24" as const },
      { name: "liquidity", type: "uint128" as const },
      { name: "amount0Max", type: "uint256" as const },
      { name: "amount1Max", type: "uint256" as const },
      { name: "recipient", type: "address" as const },
      { name: "hookData", type: "bytes" as const },
    ] as const,
    [
      {
        currency0: poolKey.currency0,
        currency1: poolKey.currency1,
        hooks: poolKey.hooks,
        poolManager: poolKey.poolManager,
        fee: poolKey.fee,
        parameters: poolKey.parameters,
      },
      tickLower,
      tickUpper,
      liquidity,
      amount0Max,
      amount1Max,
      recipient,
      "0x",
    ]
  );

  // unlockData = abi.encode(bytes actions, bytes[] params)
  return encodeAbiParameters(
    [
      { name: "actions", type: "bytes" },
      { name: "params", type: "bytes[]" },
    ] as const,
    [actionsHex, [mintParams]]
  );
}

// Min/max tick for each fee tier
const MIN_TICK = -887272;
const MAX_TICK = 887272;

export function CLLiquidityCard() {
  const { address: account, isConnected } = useAccount();

  const [tokenA, setTokenA] = useState<TokenInfo>(NATIVE_JASMY);
  const [tokenB, setTokenB] = useState<TokenInfo>(DEFAULT_TOKEN_LIST[1]);
  const [selectedFeeIdx, setSelectedFeeIdx] = useState(2); // default 0.25%
  const [minPriceInput, setMinPriceInput] = useState("");
  const [maxPriceInput, setMaxPriceInput] = useState("");
  const [amountA, setAmountA] = useState("");
  const [amountB, setAmountB] = useState("");
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();
  const feeTier = CL_FEE_TIERS[selectedFeeIdx];

  const addrA: Address = tokenA.isNative ? ADDRESSES.WJASMY : tokenA.address;
  const addrB: Address = tokenB.isNative ? ADDRESSES.WJASMY : tokenB.address;
  const [currency0, currency1] = sortCurrencies(addrA, addrB);
  const isToken0A = addrA.toLowerCase() === currency0.toLowerCase();

  const poolKey: PoolKey = {
    currency0,
    currency1,
    hooks: NO_HOOKS,
    poolManager: ADDRESSES.CL_POOL_MANAGER,
    fee: feeTier.fee,
    parameters: clParameters(feeTier.tickSpacing),
  };

  const decimals0 = isToken0A ? tokenA.decimals : tokenB.decimals;
  const decimals1 = isToken0A ? tokenB.decimals : tokenA.decimals;

  // Read pool slot0 (current tick and sqrtPrice)
  const poolIdBytes = buildPoolId(poolKey);
  const { data: slot0Data } = useReadContract({
    address: ADDRESSES.CL_POOL_MANAGER,
    abi: CL_POOL_MANAGER_ABI,
    functionName: "getSlot0",
    args: [poolIdBytes],
    query: { refetchInterval: 10_000 },
  });
  const { data: poolLiquidity } = useReadContract({
    address: ADDRESSES.CL_POOL_MANAGER,
    abi: CL_POOL_MANAGER_ABI,
    functionName: "getLiquidity",
    args: [poolIdBytes],
    query: { refetchInterval: 10_000 },
  });

  const currentTick = slot0Data ? Number((slot0Data as [bigint, number, number, number])[1]) : 0;
  const currentPrice = tickToPrice(currentTick, decimals0, decimals1);

  // Parse tick bounds from price inputs
  const minPrice = parseFloat(minPriceInput) || 0;
  const maxPrice = parseFloat(maxPriceInput) || 0;
  const rawTickLower = minPrice > 0 ? priceToTick(minPrice, decimals0, decimals1) : MIN_TICK;
  const rawTickUpper = maxPrice > 0 ? priceToTick(maxPrice, decimals0, decimals1) : MAX_TICK;
  const tickLower = nearestUsableTick(rawTickLower, feeTier.tickSpacing);
  const tickUpper = nearestUsableTick(rawTickUpper, feeTier.tickSpacing);

  const validRange = tickLower < tickUpper;

  // Balances
  const { balance: balanceA } = useTokenBalance(tokenA.isNative ? undefined : tokenA.address);
  const { balance: balanceB } = useTokenBalance(tokenB.isNative ? undefined : tokenB.address);

  const parsedA = parseTokenAmount(amountA, tokenA.decimals);
  const parsedB = parseTokenAmount(amountB, tokenB.decimals);

  // Map to currency0/currency1
  const amount0 = isToken0A ? parsedA : parsedB;
  const amount1 = isToken0A ? parsedB : parsedA;

  // Compute liquidity from amounts
  const liquidity = validRange
    ? getLiquidityForAmounts(currentTick, tickLower, tickUpper, amount0, amount1)
    : 0n;

  // Max amounts include 1% buffer for slippage
  const amount0Max = (amount0 * 101n) / 100n;
  const amount1Max = (amount1 * 101n) / 100n;

  // Approvals: CL_POSITION_MANAGER needs token approvals (non-native)
  const { needsApproval: needsApprovalA, approve: approveA, isApproving: isApprovingA } =
    useTokenAllowance(
      tokenA.isNative ? undefined : tokenA.address,
      ADDRESSES.CL_POSITION_MANAGER,
      parsedA
    );

  const { needsApproval: needsApprovalB, approve: approveB, isApproving: isApprovingB } =
    useTokenAllowance(
      tokenB.isNative ? undefined : tokenB.address,
      ADDRESSES.CL_POSITION_MANAGER,
      parsedB
    );

  const { writeContract, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Read user CL positions
  const { data: positionCount, refetch: refetchCount } = useReadContract({
    address: ADDRESSES.CL_POSITION_MANAGER,
    abi: CL_POSITION_MANAGER_ABI,
    functionName: "balanceOf",
    args: account ? [account] : undefined,
    query: { enabled: !!account, refetchInterval: 15_000 },
  });

  const posCount = positionCount ? Number(positionCount as bigint) : 0;

  // Fetch up to 10 most recent position token IDs
  const { data: tokenIdResults } = useReadContract({
    address: ADDRESSES.CL_POSITION_MANAGER,
    abi: CL_POSITION_MANAGER_ABI,
    functionName: "tokenOfOwnerByIndex",
    args: account && posCount > 0 ? [account, 0n] : undefined,
    query: { enabled: !!account && posCount > 0 },
  });

  useEffect(() => {
    if (isSuccess) {
      setAmountA("");
      setAmountB("");
      setTxHash(undefined);
      refetchCount();
    }
  }, [isSuccess, refetchCount]);

  function setFullRange() {
    setMinPriceInput("0");
    setMaxPriceInput("999999999");
  }

  const addLiquidity = useCallback(() => {
    if (!account || liquidity === 0n) return;
    const deadline = BigInt(getDeadline(20));
    const unlockData = encodeMintUnlockData(
      poolKey,
      tickLower,
      tickUpper,
      liquidity,
      amount0Max,
      amount1Max,
      account
    );

    const nativeValue = tokenA.isNative ? parsedA : tokenB.isNative ? parsedB : 0n;

    writeContract(
      {
        address: ADDRESSES.CL_POSITION_MANAGER,
        abi: CL_POSITION_MANAGER_ABI,
        functionName: "modifyLiquidities",
        args: [unlockData, deadline],
        value: nativeValue,
      },
      { onSuccess: (h) => setTxHash(h) }
    );
  }, [
    account,
    poolKey,
    tickLower,
    tickUpper,
    liquidity,
    amount0Max,
    amount1Max,
    tokenA,
    tokenB,
    parsedA,
    parsedB,
    writeContract,
  ]);

  const isLoading = isPending || isConfirming;

  function getButtonText(): string {
    if (!isConnected) return "Connect Wallet";
    if (!amountA || parseFloat(amountA) <= 0) return "Enter Amount";
    if (!validRange) return "Invalid Price Range";
    if (liquidity === 0n) return "Insufficient Liquidity";
    if (isApprovingA) return `Approving ${tokenA.symbol}...`;
    if (needsApprovalA) return `Approve ${tokenA.symbol}`;
    if (isApprovingB) return `Approving ${tokenB.symbol}...`;
    if (needsApprovalB) return `Approve ${tokenB.symbol}`;
    if (isPending) return "Confirming...";
    if (isConfirming) return "Adding Liquidity...";
    return "Add Liquidity";
  }

  function handleAction() {
    if (needsApprovalA) return approveA();
    if (needsApprovalB) return approveB();
    addLiquidity();
  }

  return (
    <div className="glass-card p-6 w-full max-w-[540px]">
      <div className="mb-5">
        <h2 className="font-display font-semibold text-xl mb-1">Concentrated Liquidity</h2>
        <p className="text-text-tertiary text-xs">
          Set a price range to concentrate your liquidity — earn up to 4000x more fees per dollar.
        </p>
      </div>

      {/* Token pair */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1">
          <div className="text-xs text-text-tertiary mb-1.5">Token A</div>
          <TokenSelector selectedToken={tokenA} onSelect={setTokenA} otherToken={tokenB} />
        </div>
        <Plus className="w-4 h-4 text-text-tertiary mt-5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-xs text-text-tertiary mb-1.5">Token B</div>
          <TokenSelector selectedToken={tokenB} onSelect={setTokenB} otherToken={tokenA} />
        </div>
      </div>

      {/* Fee tier selector */}
      <div className="mb-5">
        <div className="text-xs text-text-tertiary mb-2">Fee Tier</div>
        <div className="grid grid-cols-4 gap-1.5">
          {CL_FEE_TIERS.map((tier, i) => (
            <button
              key={tier.fee}
              onClick={() => setSelectedFeeIdx(i)}
              className={cn(
                "flex flex-col items-center py-2 px-1 rounded-lg border text-xs font-medium transition-all",
                selectedFeeIdx === i
                  ? "bg-primary/15 border-primary/30 text-primary"
                  : "bg-surface border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover"
              )}
            >
              <span className="font-semibold">{tier.label}</span>
              <span className="text-text-tertiary text-[10px] mt-0.5">
                tick {tier.tickSpacing}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Current price */}
      {slot0Data && (
        <div className="mb-4 p-3 bg-surface/30 rounded-button border border-border text-sm">
          <div className="flex items-center justify-between text-text-secondary">
            <span>Current Price</span>
            <span className="font-medium text-text-primary">
              {currentPrice > 0
                ? `${currentPrice.toFixed(6)} ${isToken0A ? tokenB.symbol : tokenA.symbol} / ${isToken0A ? tokenA.symbol : tokenB.symbol}`
                : "Pool not initialized"}
            </span>
          </div>
          <div className="flex items-center justify-between text-text-secondary mt-1">
            <span>Current Tick</span>
            <span>{currentTick}</span>
          </div>
          {poolLiquidity !== undefined && (
            <div className="flex items-center justify-between text-text-secondary mt-1">
              <span>Pool Liquidity</span>
              <span>{formatTokenAmount(poolLiquidity as bigint)}</span>
            </div>
          )}
        </div>
      )}

      {/* Price range */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-text-tertiary">Price Range</span>
          <button
            onClick={setFullRange}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover transition-colors"
          >
            <ZoomIn className="w-3 h-3" />
            Full Range
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-text-tertiary mb-1">Min Price</div>
            <input
              type="number"
              value={minPriceInput}
              onChange={(e) => setMinPriceInput(e.target.value)}
              placeholder="0.0"
              className="input-field text-sm"
              min="0"
              step="any"
            />
            {minPriceInput && (
              <div className="text-xs text-text-tertiary mt-1">
                Tick: {nearestUsableTick(
                  priceToTick(parseFloat(minPriceInput) || 0, decimals0, decimals1),
                  feeTier.tickSpacing
                )}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-text-tertiary mb-1">Max Price</div>
            <input
              type="number"
              value={maxPriceInput}
              onChange={(e) => setMaxPriceInput(e.target.value)}
              placeholder="∞"
              className="input-field text-sm"
              min="0"
              step="any"
            />
            {maxPriceInput && (
              <div className="text-xs text-text-tertiary mt-1">
                Tick: {nearestUsableTick(
                  priceToTick(parseFloat(maxPriceInput) || 0, decimals0, decimals1),
                  feeTier.tickSpacing
                )}
              </div>
            )}
          </div>
        </div>
        {!validRange && minPriceInput && maxPriceInput && (
          <p className="text-xs text-error mt-2">
            Min price must be less than max price
          </p>
        )}
      </div>

      {/* Amount inputs */}
      <div className="space-y-3 mb-5">
        <div className="token-input-row">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-text-tertiary">{tokenA.symbol} Amount</span>
            <button
              onClick={() => setAmountA(formatUnits(balanceA, tokenA.decimals))}
              className="text-xs text-text-tertiary hover:text-primary transition-colors"
            >
              Balance: {formatTokenAmount(balanceA, tokenA.decimals)}
              <span className="ml-1 text-primary font-medium">MAX</span>
            </button>
          </div>
          <input
            type="number"
            value={amountA}
            onChange={(e) => setAmountA(e.target.value)}
            placeholder="0.0"
            className="amount-input"
            min="0"
            step="any"
          />
        </div>

        <div className="flex justify-center">
          <Plus className="w-4 h-4 text-text-tertiary" />
        </div>

        <div className="token-input-row">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-text-tertiary">{tokenB.symbol} Amount</span>
            <button
              onClick={() => setAmountB(formatUnits(balanceB, tokenB.decimals))}
              className="text-xs text-text-tertiary hover:text-primary transition-colors"
            >
              Balance: {formatTokenAmount(balanceB, tokenB.decimals)}
              <span className="ml-1 text-primary font-medium">MAX</span>
            </button>
          </div>
          <input
            type="number"
            value={amountB}
            onChange={(e) => setAmountB(e.target.value)}
            placeholder="0.0"
            className="amount-input"
            min="0"
            step="any"
          />
        </div>
      </div>

      {/* Liquidity preview */}
      {validRange && liquidity > 0n && (
        <div className="mb-4 p-3 bg-surface/30 rounded-button border border-border text-sm">
          <div className="text-text-tertiary text-xs mb-2">Position Preview</div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>Liquidity</span>
            <span>{formatTokenAmount(liquidity)}</span>
          </div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>Tick Range</span>
            <span>
              {tickLower} → {tickUpper}
            </span>
          </div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>Price Range</span>
            <span className="text-xs">
              {tickToPrice(tickLower, decimals0, decimals1).toFixed(4)} →{" "}
              {tickToPrice(tickUpper, decimals0, decimals1).toFixed(4)}
            </span>
          </div>
        </div>
      )}

      <button
        onClick={handleAction}
        disabled={
          !isConnected ||
          isLoading ||
          isApprovingA ||
          isApprovingB ||
          (!needsApprovalA && !needsApprovalB && (liquidity === 0n || !validRange))
        }
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        {(isLoading || isApprovingA || isApprovingB) && (
          <Loader2 className="w-4 h-4 animate-spin" />
        )}
        {getButtonText()}
      </button>

      {txHash && (
        <div className="mt-3 text-center">
          <a
            href={explorerUrl("tx", txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
          >
            View Transaction <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {/* Existing positions */}
      {posCount > 0 && (
        <div className="mt-6">
          <div className="text-sm font-medium text-text-secondary mb-3">
            Your CL Positions ({posCount})
          </div>
          {tokenIdResults !== undefined && (
            <PositionRow
              tokenId={tokenIdResults as bigint}
              currency0Symbol={isToken0A ? tokenA.symbol : tokenB.symbol}
              currency1Symbol={isToken0A ? tokenB.symbol : tokenA.symbol}
            />
          )}
          {posCount > 1 && (
            <p className="text-xs text-text-tertiary mt-2 text-center">
              + {posCount - 1} more position{posCount > 2 ? "s" : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function PositionRow({
  tokenId,
  currency0Symbol,
  currency1Symbol,
}: {
  tokenId: bigint;
  currency0Symbol: string;
  currency1Symbol: string;
}) {
  const { data: position } = useReadContract({
    address: ADDRESSES.CL_POSITION_MANAGER,
    abi: CL_POSITION_MANAGER_ABI,
    functionName: "getPosition",
    args: [tokenId],
    query: { staleTime: 10_000 },
  });

  if (!position) {
    return (
      <div className="p-3 bg-surface/30 rounded-button border border-border text-sm text-text-tertiary">
        Loading position #{tokenId.toString()}...
      </div>
    );
  }

  const pos = position as [
    `0x${string}`,
    number,
    number,
    bigint,
    bigint,
    bigint
  ];
  const [, tickLower, tickUpper, liquidity] = pos;

  return (
    <div className="p-3 bg-surface/30 rounded-button border border-border text-sm mb-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-text-primary font-medium">
          #{tokenId.toString()} — {currency0Symbol}/{currency1Symbol}
        </span>
        <span className="text-xs text-primary">
          Liquidity: {formatTokenAmount(liquidity)}
        </span>
      </div>
      <div className="text-text-tertiary text-xs">
        Ticks: {tickLower} → {tickUpper}
      </div>
    </div>
  );
}
